"use client";

import { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import axiosInstance from "../../helpers/Axios";

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

const BulkUserUploadPage = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);
  const { access_token } = useContext(AppStateContext);

  const handleExcelFileChange = (event) => {
    setExcelFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!excelFile) {
      alert("Please upload an Excel file.");
      return;
    }

    if (!access_token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("excel_file", excelFile);

      // First, try to get the CSV file as a blob
      const response = await axiosInstance.post(
        `${baseURL}/api/trail/bulk-user-add/`,
        formData
      );

      // Check if the response is a CSV or Excel file
      const contentType = response.headers["content-type"];
      let fileExtension = "";
      let fileMime = "";
      if (contentType) {
        if (contentType.includes("text/csv")) {
          fileExtension = ".csv";
          fileMime = "text/csv";
        } else if (contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
          fileExtension = ".xlsx";
          fileMime = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        } else if (contentType.includes("application/vnd.ms-excel")) {
          fileExtension = ".xls";
          fileMime = "application/vnd.ms-excel";
        }
      }

      if (fileExtension) {
        // Download the file (Excel or CSV)
        const url = window.URL.createObjectURL(new Blob([response.data], { type: fileMime }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `bulk-users${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        setResponseData({
          status: "success",
          data: { message: `File downloaded successfully (${fileExtension})` },
        });
      } else {
        // If not a recognized file, try to parse as JSON for error/info, else show raw text
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const json = JSON.parse(reader.result);
            setResponseData(json);
          } catch (e) {
            // Not JSON, show raw text
            setResponseData({
              status: "error",
              data: { message: reader.result || "Unknown response format." },
            });
          }
        };
        reader.readAsText(response.data);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setResponseData({
        status: "error",
        message: "",
        errors: [],
        data: {
          message:
            error.response?.data?.message ||
            error.message ||
            "Upload failed! Please try again.",
        },
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setExcelFile(null);
    setResponseData(null);
  };

  const renderUserResults = () => {
    if (!responseData?.data?.results) {
      return null;
    }

    const { results } = responseData.data;

    if (results.length === 0) return null;

    return (
      <div className="mt-6 border border-gray-200 p-3 rounded-lg">
        <Typography variant="h6" className="mb-3 text-gray-800 font-medium">
          Upload Results ({results.length})
        </Typography>

        <div className="max-h-80 overflow-y-auto pr-1">
          {results.map((result, index) => {
            const isSuccess = result.status === "created" || result.status === "updated";
            const colorScheme = isSuccess
              ? {
                  bg: "bg-green-50",
                  border: "border-green-200",
                  text: "text-green-700",
                  badge: "bg-green-100 text-green-800",
                  leftBorder: "border-green-400",
                }
              : {
                  bg: "bg-red-50",
                  border: "border-red-200",
                  text: "text-red-700",
                  badge: "bg-red-100 text-red-800",
                  leftBorder: "border-red-400",
                };

            return (
              <div
                key={index}
                className={`${colorScheme.bg} p-4 rounded-lg mb-3 border ${colorScheme.border}`}
              >
                <div className="mb-2">
                  <p className={`font-medium ${colorScheme.text}`}>
                    <span>Name: </span>
                    {result.full_name || "Unknown"}
                  </p>
                  <p
                    className={`text-xs ${colorScheme.badge} px-2 py-1 mt-1 inline-block rounded`}
                  >
                    Status: {result.status}
                  </p>
                </div>

                {isSuccess ? (
                  <div className="bg-white p-3 rounded border-l-2 border-green-400 mt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-sm font-medium text-green-700">Username:</p>
                        <p className="text-sm text-green-600 font-mono">{result.username}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-green-700">Password:</p>
                        <p className="text-sm text-green-600 font-mono">{result.password}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-3 rounded border-l-2 border-red-400 mt-2">
                    <p className="text-sm font-medium text-red-700">Error:</p>
                    <p className="text-sm text-red-600 mt-1">
                      {result.error || "No error details available"}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          className="text-center mb-6 font-semibold text-gray-800"
        >
          Bulk User Upload
        </Typography>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Excel File
            </label>
            <TextField
              type="file"
              accept=".xlsx, .xls"
              onChange={handleExcelFileChange}
              fullWidth
              variant="outlined"
              placeholder="Choose Excel file"
              InputProps={{
                startAdornment: (
                  <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
                ),
              }}
            />
          </div>
          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            className="py-2.5"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                <PersonAddIcon className="w-5 h-5 mr-2" />
                Upload Users
              </>
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">
                Bulk Upload Results
              </Typography>
              <IconButton
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <DialogContent className="px-6 py-5">
            {responseData && (
              <div className="space-y-4">
                <div
                  className={`flex items-center space-x-3 ${
                    responseData.status === "success"
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  } p-4 rounded-lg`}
                >
                  {responseData.status === "success" ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ErrorIcon className="h-5 w-5 text-red-500" />
                  )}
                  <p className="font-medium">{responseData.data?.message}</p>
                </div>

                {responseData.status === "success" && responseData.data && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Total Processed
                      </Typography>
                      <Typography variant="h6" className="text-gray-900">
                        {responseData.data.total_processed}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Created/Updated
                      </Typography>
                      <Typography variant="h6" className="text-green-600">
                        {responseData.data.created_or_updated}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Failed
                      </Typography>
                      <Typography variant="h6" className="text-red-600">
                        {responseData.data.failed}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Success Rate
                      </Typography>
                      <Typography variant="h6" className="text-blue-600">
                        {responseData.data.total_processed > 0
                          ? Math.round(
                              (responseData.data.created_or_updated /
                                responseData.data.total_processed) *
                                100
                            )
                          : 0}
                        %
                      </Typography>
                    </ResultCard>
                  </div>
                )}

                {renderUserResults()}
              </div>
            )}
          </DialogContent>

          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">
                Close
              </StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default BulkUserUploadPage;